<script setup lang="ts">
// Detail for the selected entity: summary, quick controls, metrics,
// sparklines, and this entity's events with full error text.
import { computed, onMounted, reactive, ref, useTemplateRef, watch } from 'vue'
import type { Destination, Encode, Event, Input, Output } from '../api'
import { useNode } from '../store'
import { faster } from '../encoding'
import {
    ago,
    bytes,
    cpuShare,
    dateTime,
    channelsText,
    dot,
    dotClass,
    dur,
    entityLabel,
    etrDescribe,
    etrErrors,
    fpsLabel,
    kindTag,
    probeText,
    probed,
    inWindow,
    listed,
    mbps,
    pct as cpuPct,
    secretText,
    maskRTMP,
    maskURL,
    maskURLs,
} from '../util'
import Sparkline from './Sparkline.vue'
import ConfirmDialog from './ConfirmDialog.vue'
import EventDialog from './EventDialog.vue'
import Meters from './Meters.vue'
import DetailTabs from './DetailTabs.vue'
import SourceAudio from './detail/SourceAudio.vue'
import Figure from './detail/Figure.vue'
import StatusSlot from './detail/StatusSlot.vue'
import { pickTab, rememberTab, rememberedTab, tabsFor, type TabId } from '../detailtabs'
import AudioHistory from './AudioHistory.vue'
import { measured } from '../audiohist'
import { scheduleNote } from '../schedule'
import { breakText, nextBreakText } from '../scte35'
import { api, virtualConfig, type EditTarget, type EncodeStatus, type InputStatus } from '../api'
import Toggle from './Toggle.vue'
import InputPreview from './detail/InputPreview.vue'
import MonitorPlayer from './detail/MonitorPlayer.vue'
import PullClients from './detail/PullClients.vue'
import LinkStats from './detail/LinkStats.vue'
import CasterStats from './detail/CasterStats.vue'
import StreamPreview from './detail/StreamPreview.vue'
import MachineDetail from './detail/MachineDetail.vue'
import MonitorDetail from './detail/MonitorDetail.vue'
import StorageDetail from './detail/StorageDetail.vue'
import { useSeries } from './detail/series'

const emit = defineEmits<{ edit: [EditTarget] }>()
const node = useNode()
const sel = computed(() => node.selection)
// on a narrow screen the panel sits below the list: show it when something is picked
const panelEl = useTemplateRef<HTMLDivElement>('panel')
const toPanel = () => {
    if (sel.value && window.matchMedia('(max-width: 900px)').matches) panelEl.value?.scrollIntoView({ block: 'start' })
}
// the panel mounts with the first pick (it shows only for one), then follows
onMounted(toPanel)
watch(sel, toPanel, { flush: 'post' }) // after the panel shows the new selection: its height is what the scroll is to
const cfg = computed(() => node.config!)
const st = computed(() => node.status!)
const now = computed(() => node.now) // the store's clock (the node's)

const input = computed(() =>
    sel.value?.kind === 'input' ? cfg.value.inputs.find((i) => i.id === sel.value!.id) : undefined,
)
const encode = computed(() =>
    sel.value?.kind === 'encode' ? cfg.value.encodes.find((e) => e.id === sel.value!.id) : undefined,
)
const dest = computed(() =>
    sel.value?.kind === 'destination' ? cfg.value.destinations.find((d) => d.id === sel.value!.id) : undefined,
)
// a destination's encode: what watching the destination hears (its tracks)
const destEncode = computed(() => cfg.value.encodes.find((e) => e.id === dest.value?.encode_id))
const machine = computed(() => (sel.value?.kind === 'machine' ? st.value.machine : undefined))
const output = computed(() =>
    sel.value?.kind === 'output' ? cfg.value.outputs?.find((o) => o.id === sel.value!.id) : undefined,
)
const storage = computed(() =>
    sel.value?.kind === 'storage' ? cfg.value.storage?.find((s) => s.id === sel.value!.id) : undefined,
)
const storageSt = computed(() =>
    storage.value ? st.value.storage?.find((s) => s.id === storage.value!.id) : undefined,
)
// a just-created entity has no status until the next 1 Hz tick: show it as
// starting rather than crashing on an undefined snapshot
const placeholder = { state: 'starting', since: new Date().toISOString(), restarts: 0 }
const snap = computed(() =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- any for the entity lists (below)
    !sel.value
        ? undefined
        : sel.value.kind === 'machine'
          ? st.value.machine
          : sel.value.kind === 'storage'
            ? (storageSt.value ?? placeholder)
            : // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access -- read field by field per kind below
              ((st.value as any)[sel.value.kind + 's']?.[sel.value.id] ?? placeholder),
)
// the window's events, and the problem already going on when it starts (a
// no signal since before it: the bar is red from its first pixel)
const events = computed(() => {
    if (!sel.value) return []
    const [from, to] = node.span
    return inWindow(node.entityEvents(sel.value.kind, sel.value.id), () => true, { from, to })
        .filter(listed)
        .reverse()
})

const { series, loading: seriesLoading, times, graphFrom, graphLabel } = useSeries(sel)

// an input's source format split for the figure: its size, then the rest
// (rate, codec), each on its own line
const srcFmt = computed(() => {
    const src = (snap.value as InputStatus | undefined)?.source ?? ''
    const i = src.indexOf(' ')
    return i < 0 ? [src, ''] : [src.slice(0, i), src.slice(i + 1)]
})
// pull destinations: where readers connect, one line per node address
const isPullDest = computed(
    () => dest.value?.kind === 'rtmp_pull' || dest.value?.kind === 'srt_listener' || dest.value?.kind === 'hls_pull',
)
// and whose readers the node counts (an HLS origin's are requests, not connections)
const countsReaders = computed(() => isPullDest.value && dest.value?.kind !== 'hls_pull')
// tabs (detailtabs.ts): the kind's, the one chosen last for the kind; above
// them what they read, or the immediate watch below throws on a panel
// opened with an entity already selected (a blank panel)
const tabs = computed(() =>
    sel.value
        ? tabsFor(sel.value.kind, {
              caster: input.value?.kind === 'caster',
              pull: countsReaders.value,
              health: !virtualConfig,
              monitoring: !virtualConfig && node.config?.mode !== 'player',
          })
        : [],
)
const tab = ref<TabId | ''>('')
watch(
    () => [sel.value?.kind, sel.value?.id, tabs.value.map((x) => x.id).join()],
    () => (tab.value = sel.value ? pickTab(tabs.value, rememberedTab(sel.value.kind)) : ''),
    { immediate: true },
)
watch(tab, (t) => {
    if (t && sel.value) rememberTab(sel.value.kind, t)
})
// a dot on a tab not shown: error or warning events not yet seen on the
// Events tab (this session), a caster's line not good or its return refused
const eventsSeen = reactive(new Map<string, number>())
watch([tab, () => events.value.length], () => {
    if (tab.value === 'events' && sel.value) eventsSeen.set(sel.value.kind + '/' + sel.value.id, Date.now())
})
const dots = computed(() => {
    const out: Partial<Record<TabId, 'bad' | 'warn'>> = {}
    if (!sel.value) return out
    const seen = eventsSeen.get(sel.value.kind + '/' + sel.value.id) ?? 0
    const unseen = events.value.filter((e) => Date.parse(e.ts) > seen)
    if (unseen.some((e) => e.severity === 'error')) out.events = 'bad'
    else if (unseen.some((e) => e.severity === 'warning')) out.events = 'warn'
    const c = (snap.value as InputStatus | undefined)?.caster
    if (c?.connected && (c.page?.health === 'poor' || c.return_refused)) out.caster = 'bad'
    else if (c?.connected && c.page?.health === 'fair') out.caster = 'warn'
    return out
})
// an encode's set bitrate (video and audio; none for a copy) and key frame
// interval in seconds (its GOP in frames at the frame rate it runs at)
const targetK = computed(() =>
    !encode.value || encode.value.passthrough
        ? 0
        : encode.value.twitch
          ? twitchK.value
          : encode.value.video.bitrate_k + (encode.value.audio ?? []).reduce((a, t) => a + (t.bitrate_k ?? 0), 0),
)
// a passthrough's delay buffer: the kbit/s the node sized it for (the figure
// set, 25000 while the source's rate is unknown, or the measured rate that
// grew it); the config's figure until the node reports
const bufferK = computed(
    () => (snap.value as EncodeStatus | undefined)?.buffer_k || encode.value?.video.bitrate_k || 25000,
)
// a Twitch Enhanced Broadcasting encode: what Twitch asked for at its start
const twitch = computed(() => (encode.value?.twitch ? (snap.value as EncodeStatus | undefined)?.twitch : undefined))
const twitchK = computed(
    () => (twitch.value?.renditions ?? []).reduce((a, r) => a + r.bitrate_k, 0) + (twitch.value?.audio_k ?? 0),
)
const gopSeconds = computed(() => {
    const e = encode.value
    if (!e || e.passthrough || e.twitch || !e.video.gop) return 0
    // the input's rate as set ("50", "60000/1001"); auto: what it runs at
    const set = cfg.value.inputs.find((i) => i.id === e.input_id)?.fps ?? ''
    const [num = 0, den = 1] = set.split('/').map(Number)
    const fps = num > 0 ? num / (den || 1) : ((snap.value as { fps?: number } | undefined)?.fps ?? 0)
    return fps > 1 ? e.video.gop / fps : 0
})
// an SRT link in the window (or now): its round trip and losses are graphed
const hasLink = computed(() => {
    const sn = snap.value as { srt?: unknown; socket?: { tcp?: unknown } } | undefined
    return series.value.some((s) => s.rtt_ms) || !!sn?.srt || !!sn?.socket?.tcp
})
// which link the round trip and losses are of: SRT, or a TCP connection (resent segments)
const linkName = computed(() =>
    (snap.value as { srt?: unknown } | undefined)?.srt ? 'SRT' : input.value?.kind === 'caster' ? 'WebRTC' : 'TCP',
)
// an encode's tracks carry the peaks of the source channels they map
const trackMeters = computed(() => {
    if (!encode.value) return []
    const src = st.value.inputs[encode.value.input_id]?.peak_db
    if (!src) return []
    return encode.value.audio.map((a, ti) => ({
        name: a.name || `track ${ti + 1}`,
        layout: a.layout,
        db: a.channel_map.map((c) => src[c] ?? -100),
        labels: a.channel_map.map((c, o) => `${o + 1}←${c + 1}`),
    }))
})
// where a remote encoder should push for listener inputs, one line per address
const pushTargets = computed((): { label: string; url: string }[] => {
    const i = input.value
    if (!i || !labelledAddrs.value.length) return []
    if (i.kind === 'srt_listener') {
        const q = [
            'mode=caller',
            ...(i.srt?.latency_ms ? [`latency=${i.srt.latency_ms * 1000}`] : []),
            ...(i.srt?.passphrase ? [`passphrase=${secretText(i.srt.passphrase, 'passphrase')}`] : []),
            ...(i.srt?.streamid ? [`streamid=${i.srt.streamid}`] : []),
        ]
        return labelledAddrs.value.map(({ ip, label }) => ({ label, url: `srt://${ip}:${i.port}?${q.join('&')}` }))
    }
    if (i.kind === 'rtmp_listener' && i.url) {
        const m = i.url.match(/^rtmp:\/\/[^/]+(\/.*)?$/)
        const portMatch = i.url.match(/:(\d+)(\/|$)/)
        return labelledAddrs.value.map(({ ip, label }) => ({
            label,
            url: `rtmp://${ip}${portMatch ? `:${portMatch[1]!}` : ''}${m?.[1] ?? '/'}`,
        }))
    }
    return []
})
// the node's addresses with a label: the public one first (its own hourly
// lookup; reachable from outside once the port is forwarded), then every
// private interface
const labelledAddrs = computed(() => {
    const m = st.value.machine
    const out: { ip: string; label: string }[] = []
    if (m?.public_ip) out.push({ ip: m.public_ip, label: 'public' })
    for (const a of m?.addresses ?? []) if (a !== m?.public_ip) out.push({ ip: a, label: 'private' })
    return out
})
const pullTargets = computed(() => {
    const d = dest.value
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- snap is per kind (above)
    const port = snap.value?.port as number | undefined
    if (!d || !isPullDest.value || !port) return []
    const pass = d.pull_auth ? secretText(d.pull_auth.pass, 'password') : ''
    const cred = d.pull_auth ? `${d.pull_auth.user}:${pass}` : ''
    return labelledAddrs.value.map(({ ip, label }) => ({
        label,
        url:
            d.kind === 'rtmp_pull'
                ? `rtmp://${ip}:${port}/${d.stream || d.id}${cred ? `?user=${d.pull_auth!.user}&pass=${pass}` : ''}`
                : d.kind === 'hls_pull'
                  ? `http://${cred ? cred + '@' : ''}${ip}:${port}/${d.stream || d.id}/index.m3u8`
                  : `srt://${ip}:${port}?streamid=read:${d.stream || d.id}${cred ? ':' + cred : ''}${d.srt?.passphrase ? `&passphrase=${secretText(d.srt.passphrase, 'passphrase')}` : ''}`,
    }))
})
const copy = (text: string) => node.copy(text)

const confirm = ref<{ title: string; body: string; label: string; danger?: boolean; run: () => Promise<void> } | null>(
    null,
)
const openEvent = ref<Event | null>(null)

function toggleDest() {
    const d = dest.value!
    confirm.value = {
        title: d.enabled ? `Disable ${d.name}?` : `Enable ${d.name}?`,
        body: d.enabled
            ? 'The stream to this destination stops immediately. Other destinations are not affected.'
            : 'The destination connects and starts sending from exactly now minus its delay.',
        label: d.enabled ? 'Disable' : 'Enable',
        run: () => node.patch<Destination>('destinations', d.id, (c) => ({ ...c, enabled: !d.enabled })),
    }
}
function toggleSlate() {
    const i = input.value!
    confirm.value = {
        title: i.slate_enabled ? `Remove slate from ${i.name}?` : `Show slate on ${i.name}?`,
        body: i.slate_enabled
            ? 'The slate disappears from every encode of this input.'
            : `“${i.slate_text || (i.slate_info ? 'node, input, format, state and clock' : i.name)}” is drawn on every encode of this input.`,
        label: i.slate_enabled ? 'Remove slate' : 'Show slate',
        run: () => node.patch<Input>('inputs', i.id, (c) => ({ ...c, slate_enabled: !i.slate_enabled })),
    }
}
function toggleOverlay() {
    const i = input.value!
    confirm.value = {
        title: i.overlay_enabled ? `Remove overlay from ${i.name}?` : `Show overlay on ${i.name}?`,
        body: i.overlay_enabled
            ? 'The picture disappears from every encode of this input.'
            : 'The picture is drawn over every encode of this input.',
        label: i.overlay_enabled ? 'Remove overlay' : 'Show overlay',
        run: () => node.patch<Input>('inputs', i.id, (c) => ({ ...c, overlay_enabled: !i.overlay_enabled })),
    }
}
function toggleEncodeSlate() {
    const e = encode.value!
    confirm.value = {
        title: e.slate_enabled ? `Remove the slate from ${e.name}?` : `Show a slate on ${e.name}?`,
        body: e.slate_enabled
            ? 'The slate disappears from this encode.'
            : `“${e.slate_text || e.name}” is drawn at the top of this encode only.`,
        label: e.slate_enabled ? 'Remove slate' : 'Show slate',
        run: () => node.patch<Encode>('encodes', e.id, (c) => ({ ...c, slate_enabled: !e.slate_enabled })),
    }
}
function toggleEncode() {
    const e = encode.value!
    confirm.value = {
        title: e.enabled ? `Disable ${e.name}?` : `Enable ${e.name}?`,
        body: e.enabled
            ? 'The encoder stops, and its destinations get no stream until it is enabled again.'
            : 'The encoder starts and its destinations get its stream again.',
        label: e.enabled ? 'Disable' : 'Enable',
        run: () => node.patch<Encode>('encodes', e.id, (c) => ({ ...c, enabled: !e.enabled })),
    }
}
// an encode that falls behind (loses frames): faster settings to pick
// from, never applied by the node (DECISIONS 2026-10-09 "encode guidance");
// no dialog of its own: the node's plan asks before the restart
const hasNVIDIA = computed(() => (st.value.machine?.gpus?.length ?? 0) > 0)
const encodeFaster = computed(() => {
    const e = encode.value
    return e && (snap.value as EncodeStatus | undefined)?.falls_behind
        ? faster(e.video, e.advanced, hasNVIDIA.value)
        : []
})
function useFaster(label: string) {
    node.patch<Encode>('encodes', encode.value!.id, (c) => {
        const o = faster(c.video, c.advanced, hasNVIDIA.value).find((x) => x.label === label)
        return o ? { ...c, video: o.video, advanced: o.advanced } : c
    }).catch(() => {})
}
// a player: its screen, and putting an input on it (no confirmation: choosing
// in the select, or the button, is the deliberate act)
const player = computed(() => cfg.value.mode === 'player')
const screen = computed(() => (player.value ? cfg.value.outputs?.[0] : undefined))
function showOnScreen(inputId: string) {
    const o = screen.value
    // save reports a failure itself (a toast); nothing more to do with it here
    if (o && o.input_id !== inputId)
        node.patch<Output>('outputs', o.id, (c) => ({ ...c, input_id: inputId })).catch(() => {})
}
function toggleOutput() {
    const o = output.value!
    confirm.value = {
        title: o.enabled ? `Disable ${o.name}?` : `Enable ${o.name}?`,
        body: o.enabled ? 'Playout on this output stops immediately.' : 'Playout of its input starts on this output.',
        label: o.enabled ? 'Disable' : 'Enable',
        run: () => node.patch<Output>('outputs', o.id, (c) => ({ ...c, enabled: !o.enabled })),
    }
}
function del() {
    const s = sel.value!
    const name = (input.value ?? encode.value ?? dest.value ?? output.value ?? storage.value)!.name
    const kind =
        s.kind === 'input'
            ? 'inputs'
            : s.kind === 'encode'
              ? 'encodes'
              : s.kind === 'output'
                ? 'outputs'
                : s.kind === 'storage'
                  ? 'storage'
                  : 'destinations'
    // what refers to it: said before asking, and dealt with in the same delete
    const names = (xs: { name: string }[]) => xs.map((x) => x.name).join(', ')
    let body = 'This cannot be undone.'
    let label = 'Delete'
    let query = ''
    if (s.kind === 'input') {
        const encs = cfg.value.encodes.filter((e) => e.input_id === s.id)
        const outs = (cfg.value.outputs ?? []).filter((o) => o.input_id === s.id)
        if (encs.length || outs.length) {
            body =
                'In use by ' +
                [
                    encs.length ? `encode${encs.length > 1 ? 's' : ''} ${names(encs)}` : '',
                    outs.length ? `output${outs.length > 1 ? 's' : ''} ${names(outs)}` : '',
                ]
                    .filter(Boolean)
                    .join(' and ') +
                '.' +
                (encs.length
                    ? ' The encodes stay without an input and stop until you pick another in their settings.'
                    : '') +
                (outs.some((o) => o.kind !== 'hdmi') ? ' Its SDI and NDI outputs are deleted with it.' : '')
            label = 'Delete anyway'
            query = 'force=1'
        }
    } else if (s.kind === 'encode') {
        const ds = cfg.value.destinations.filter((d) => d.encode_id === s.id)
        if (ds.length) {
            body = `Its destinations are deleted with it: ${names(ds)}. This cannot be undone.`
            label = `Delete encode and ${ds.length} destination${ds.length === 1 ? '' : 's'}`
            query = 'with_destinations=1'
        }
    } else if (s.kind === 'storage') {
        body = 'The node unmounts it; recordings onto it must be moved or removed first. Files on the share stay.'
    } else if (s.kind === 'destination') {
        const gs = (cfg.value.groups ?? []).filter((g) => g.destinations.includes(s.id))
        if (gs.length) body = `It is also taken out of group ${names(gs)}. This cannot be undone.`
    }
    // a multiview tile showing it just goes empty
    const shownIn = cfg.value.inputs.filter((i) => i.multiview?.tiles.some((t) => t.source === `${s.kind}:${s.id}`))
    if (shownIn.length) body += ` Its tile in multiview ${names(shownIn)} goes empty.`
    confirm.value = {
        title: `Delete ${s.kind} ${name}?`,
        body,
        label,
        danger: true,
        run: () => node.remove(kind, s.id, query),
    }
}
async function runConfirm() {
    const c = confirm.value!
    confirm.value = null
    try {
        await c.run()
    } catch {
        /* toast shown by store */
    }
}
// an input's backup (failover.go): which one, and whether its encodes read it now
const failover = computed(() => (input.value ? (snap.value as InputStatus | undefined)?.failover : undefined))
const inputName = (id: string) => cfg.value.inputs.find((i) => i.id === id)?.name ?? id
const switching = ref(false)
async function useBackup(use: 'backup' | 'main') {
    if (!input.value) return
    switching.value = true
    try {
        await api.failover(input.value.id, use)
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
    } finally {
        switching.value = false
    }
}
// an encode's ad breaks (SCTE-35, node cues.go): the break its cues said,
// why they do not go out, and the length of the next one (0: until ended)
const encSnap = computed(() => (encode.value?.scte35 ? (snap.value as EncodeStatus | undefined) : undefined))
// what the status slot's second line says: the entity's error (a warning
// while degraded, a note while idle), else an encode's ad-marker note
const note = computed(() => {
    const s = snap.value as { state: string; error?: string } | undefined
    if (s?.error) {
        const tone = s.state === 'degraded' ? 'warn' : s.state === 'idle' ? 'note' : 'bad'
        const label = s.state === 'degraded' ? 'warning' : s.state === 'idle' ? 'note' : 'last error'
        return { tone, label, text: s.error } as const
    }
    if (encSnap.value?.scte35) return { tone: 'warn', label: 'ad markers', text: encSnap.value.scte35 } as const
    return { tone: 'ok', label: '', text: '' } as const
})
const breakS = ref(60)
const cueing = ref(false)
async function cue(type: 'out' | 'in') {
    if (!encode.value) return
    cueing.value = true
    try {
        await api.cue(
            encode.value.id,
            type === 'out' ? { type, duration_s: Math.max(0, Number(breakS.value) || 0) } : { type },
        )
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
    } finally {
        cueing.value = false
    }
}
// a destination's schedule and its groups', as they stand now (schedule.ts)
const destSchedule = computed(() => {
    const d = dest.value
    if (!d) return []
    const at = new Date(now.value)
    const own = scheduleNote(d.schedule, at)
    return [
        ...(own ? [own] : []),
        ...(cfg.value.groups ?? [])
            .filter((g) => g.destinations.includes(d.id) && scheduleNote(g.schedule, at))
            .map((g) => `${g.name}: ${scheduleNote(g.schedule, at)}`),
    ]
})
const delayDraft = ref<number | null>(null)
async function applyDelay() {
    if (delayDraft.value === null || !dest.value) return
    try {
        const delay = delayDraft.value
        await node.patch<Destination>('destinations', dest.value.id, (c) => ({ ...c, delay_seconds: delay }))
    } catch {
        return // reported by save; the draft stays for another try
    }
    delayDraft.value = null
}
// an entity's CPU as a share of the whole machine
const machineCores = computed(() => node.status?.machine.specs.cores ?? 0)
const share = (cores: number) => cpuShare(cores, machineCores.value)
// entities with CPU of their own (a passthrough reads its receiver in process)
const hasCPU = computed(() => (!!encode.value && !encode.value.passthrough) || !!input.value || !!output.value)
// what receives this input while nothing decodes it: a player's screen, or
// an IP input that skips decoding (its passthroughs, if any: the receiver
// gets the source once). Otherwise every IP input is decoded, whatever
// passes it through.
const passedBy = computed(() => {
    const id = input.value?.id
    if (player.value && input.value) return { name: 'the screen' } // a player's relay makes key-frame thumbnails
    if (
        !id ||
        !input.value?.skip_decode ||
        cfg.value.encodes.some((e) => !e.passthrough && e.input_id === id) ||
        (cfg.value.outputs ?? []).some((o) => o.input_id === id)
    )
        return undefined
    const pts = cfg.value.encodes.filter((e) => e.passthrough && e.input_id === id)
    return { name: pts.map((e) => e.name).join(', ') }
})
const encodeSummary = (id: string) => {
    const e = cfg.value.encodes.find((x) => x.id === id)
    return e ? e.name : id
}
const inputSummary = (id: string) => {
    const i = cfg.value.inputs.find((x) => x.id === id)
    return i ? i.name : id || 'no input'
}
</script>

<template>
    <!-- no overflow here: App.vue makes it scroll (a class here would win over it) -->
    <div ref="panel" class="scroll-mt-2 card">
        <div v-if="!sel" class="flex h-full min-h-40 items-center justify-center px-6 text-center text-fg-3">
            Select an input, encode or destination to see details and events.
        </div>
        <template v-else>
            <div class="flex min-h-11 items-center gap-2 border-b border-line chrome px-3 py-1.5">
                <span :class="dotClass(snap?.state ?? '')" />
                <b class="min-w-0 flex-1 truncate"
                    ><span
                        v-if="sel && kindTag(sel.kind)"
                        class="mr-1.25 inline-block min-w-8 rounded-[3px] bg-bg-3 px-1 text-center align-[1px] font-mono text-[10px] font-normal text-fg-2"
                        >{{ kindTag(sel.kind) }}</span
                    >{{ machine ? 'Machine' : (input ?? encode ?? dest ?? output ?? storage)?.name }}</b
                >
                <template v-if="!machine && !node.readOnly">
                    <button
                        class="btn-ghost text-xs"
                        @click="emit('edit', { kind: sel.kind, id: sel.id } as EditTarget)"
                    >
                        Edit
                    </button>
                    <button class="btn-ghost text-xs" @click="del">Delete</button>
                </template>
                <button class="btn-ghost text-xs" aria-label="Hide panel" @click="node.closePanel()">✕</button>
            </div>
            <div class="px-3 py-2.5">
                <!-- summary line -->
                <div v-if="input && input.fallback_media_id" class="text-xs text-fg-2">
                    on signal loss: picture (after 2 s)
                </div>
                <div v-if="input" class="text-xs text-fg-2">
                    {{ input.kind.replace('_', ' ') }}<template v-if="input.url"> · {{ maskURL(input.url) }}</template
                    ><template v-if="input.port"> · port {{ input.port }}</template
                    ><template v-if="input.device"> · {{ input.device }}</template> ·
                    {{ input.width ? `${input.width}×${input.height}` : 'auto size' }} @
                    {{
                        input.fps
                            ? fpsLabel(input.fps)
                            : input.kind === 'caster'
                              ? fpsLabel('60000/1001') + ' (the default)'
                              : 'auto rate'
                    }}
                </div>
                <div v-if="encode?.passthrough" class="text-xs text-fg-2">
                    {{ inputSummary(encode.input_id) }} → passthrough: the source's stream copied to the destinations,
                    not re-encoded · delay buffer sized for {{ bufferK }}k
                </div>
                <template v-else-if="encode?.twitch">
                    <div class="text-xs text-fg-2">
                        {{ inputSummary(encode.input_id) }} → Twitch Enhanced Broadcasting · gpu{{ encode.video.gpu }} ·
                        channels {{ encode.audio[0]?.channel_map.join('+') }}
                        <template v-if="twitch"> · {{ twitch.ingest }}</template>
                    </div>
                    <div v-if="twitch" class="text-xs text-fg-2">
                        <span class="font-mono tabular-nums">{{
                            twitch.renditions
                                .map((r) => `${r.height}p${r.fps} ${mbps(r.bitrate_k)}`)
                                .concat(`AAC ${twitch.audio_k}k`)
                                .join(' · ')
                        }}</span>
                        <template v-if="twitch.encode_percent">
                            · Twitch expects {{ twitch.encode_percent }}% of NVENC</template
                        >
                    </div>
                    <div v-else class="text-xs text-fg-3">asks Twitch for its renditions at its start</div>
                    <div v-if="twitch?.warning" class="text-xs text-warn">Twitch: {{ twitch.warning }}</div>
                    <div v-if="twitch?.fallback" class="text-xs text-warn" data-testid="twitch-fallback">
                        Fallback: Twitch's configuration service could not be reached ({{ twitch.fallback }}). One
                        rendition goes to Twitch's standard ingest; Twitch is asked again at the encode's next start.
                    </div>
                </template>
                <div v-else-if="encode" class="text-xs text-fg-2">
                    {{ inputSummary(encode.input_id) }} → {{ encode.video.codec
                    }}<template v-if="encode.video.codec.endsWith('nvenc')"> gpu{{ encode.video.gpu }}</template> ·
                    {{ encode.video.rc }} {{ encode.video.bitrate_k }}k · gop {{ encode.video.gop
                    }}<template v-if="encode.video.width">
                        · {{ encode.video.width }}×{{ encode.video.height }}</template
                    ><template v-if="encode.video.fps"> @ {{ fpsLabel(encode.video.fps) }}</template> ·
                    {{ encode.audio.length }} audio track{{ encode.audio.length > 1 ? 's' : '' }}
                </div>
                <div v-if="dest?.origin" class="text-xs text-fg-2" data-test="dest-origin-panel">
                    for
                    <a v-if="node.central" :href="`/broadcasters?id=${dest.origin.broadcaster}`">{{
                        dest.origin.broadcaster_name
                    }}</a
                    ><b v-else>{{ dest.origin.broadcaster_name }}</b> · {{ dest.origin.feed_name }}
                </div>
                <div v-if="dest" class="text-xs text-fg-2">
                    {{ encodeSummary(dest.encode_id) }} →
                    {{
                        dest.kind === 'file'
                            ? 'recording'
                            : dest.kind === 'twitch_eb'
                              ? 'Twitch Enhanced Broadcasting'
                              : dest.kind.replace('_', ' ')
                    }}
                    ·
                    <span class="font-mono tabular-nums">{{
                        dest.kind === 'file'
                            ? `${dest.path} · ${dest.container === 'mp4' ? 'MP4' : 'TS'} · ${dest.open_ended ? 'one file per recording' : `${dest.segment_seconds || 600} s chunks`}${dest.keep_days ? ` · keep ${dest.keep_days} d` : ''}`
                            : isPullDest
                              ? `served by this node${dest.kind === 'hls_pull' ? ' as HLS' : ''}${dest.pull_auth ? ' · with credentials' : ' · open'}${dest.max_clients ? ` · max ${dest.max_clients} clients` : ''}`
                              : dest.kind === 'twitch_eb'
                                ? 'where Twitch says, with the key it gave'
                                : dest.kind === 'rtmp_push'
                                  ? maskRTMP(dest.url ?? '')
                                  : maskURL(dest.url ?? '')
                    }}</span>
                </div>
                <div v-if="output && output.kind === 'hdmi'" class="text-xs text-fg-2">
                    {{ output.input_id ? inputSummary(output.input_id) : 'nothing' }} → HDMI
                    {{ output.device || '(first connected screen)'
                    }}{{ output.display_mode ? ' · ' + output.display_mode : ''
                    }}{{
                        ' · on loss ' +
                        (output.on_loss === 'black'
                            ? 'black'
                            : output.on_loss === 'slate'
                              ? `“${output.slate_text || 'No signal'}”`
                              : 'freeze')
                    }}
                </div>
                <div v-else-if="output" class="text-xs text-fg-2">
                    {{ inputSummary(output.input_id) }} →
                    {{
                        output.kind === 'decklink'
                            ? 'SDI ' + output.device
                            : output.kind === 'ndi'
                              ? 'NDI ' + (output.ndi_name || output.name)
                              : output.kind === 'alsa'
                                ? `${channelsText(output.audio_channels)} to the sound card ${output.device}`
                                : output.kind === 'aes67'
                                  ? `${channelsText(output.audio_channels)} as AES67 to ${output.aes67?.address}:${output.aes67?.port || 5004} from ${output.aes67?.interface}`
                                  : 'null (test)'
                    }}
                </div>
                <div v-if="encode && encode.audio.length > 1" class="text-xs text-fg-2">
                    {{ encode.audio.length }} audio tracks:
                    {{
                        encode.audio
                            .map((a, i) => `${a.name || 'track ' + (i + 1)} ${a.layout} [${a.channel_map.join(',')}]`)
                            .join(' · ')
                    }}
                </div>

                <!-- network storage -->
                <StorageDetail v-if="storage" :storage="storage" :storage-st="storageSt" />

                <!-- quick controls (none for the machine or a storage) -->
                <!-- a viewer (central roles.go) sees them, disabled -->
                <fieldset
                    v-if="input || encode || dest || output"
                    :disabled="node.readOnly"
                    class="m-0 my-2.5 flex min-w-0 items-center gap-2 border-0 p-0"
                >
                    <template v-if="dest">
                        <Toggle
                            :on="dest.enabled"
                            :label="dest.enabled ? 'Disable' : 'Enable'"
                            @click="toggleDest"
                        /><span class="text-xs">{{ dest.enabled ? 'enabled' : 'disabled' }}</span>
                        <span
                            v-if="destSchedule.length"
                            class="truncate text-xs text-fg-2"
                            :title="'schedule: ' + destSchedule.join('; ')"
                            data-test="dest-schedule"
                            >· {{ destSchedule.join(' · ') }}</span
                        >
                        <span class="min-w-0 flex-1" />
                        <label class="m-0 text-xs">delay</label>
                        <input
                            class="w-22.5 font-mono tabular-nums"
                            type="number"
                            min="0"
                            :max="cfg.max_buffer_seconds"
                            step="1"
                            :value="delayDraft ?? dest.delay_seconds"
                            @input="delayDraft = Number(($event.target as HTMLInputElement).value)"
                            @keyup.enter="applyDelay"
                        />
                        <span class="text-xs text-fg-2">s</span>
                        <button
                            class="w-15.5 text-xs"
                            :class="{ 'btn-primary': delayDraft !== null && delayDraft !== dest.delay_seconds }"
                            :disabled="delayDraft === null || delayDraft === dest.delay_seconds"
                            @click="applyDelay"
                        >
                            Apply
                        </button>
                    </template>
                    <template v-if="input && player">
                        <span v-if="screen?.input_id === input.id" class="text-xs text-fg-2">on screen</span>
                        <button v-else-if="screen" class="text-xs" @click="showOnScreen(input.id)">
                            Show on screen
                        </button>
                    </template>
                    <span v-else-if="input && passedBy" class="text-xs text-fg-2"
                        >{{ passedBy.name ? `passed through by ${passedBy.name}, ` : '' }}not decoded (skip decoding:
                        slate and overlay apply once something decodes it)</span
                    >
                    <template v-else-if="input">
                        <Toggle :on="input.slate_enabled" label="Slate" @click="toggleSlate" /><span class="text-xs"
                            >slate {{ input.slate_enabled ? 'on' : 'off'
                            }}<span v-if="input.slate_text" class="text-fg-2"> · “{{ input.slate_text }}”</span></span
                        >
                        <template v-if="input.overlay_media_id"
                            ><span class="w-2" /><Toggle
                                :on="!!input.overlay_enabled"
                                label="Overlay"
                                @click="toggleOverlay"
                            /><span class="text-xs">overlay {{ input.overlay_enabled ? 'on' : 'off' }}</span></template
                        >
                    </template>
                    <template v-if="encode">
                        <Toggle :on="encode.enabled" label="Enabled" @click="toggleEncode" /><span class="text-xs">{{
                            encode.enabled ? 'enabled' : 'disabled'
                        }}</span>
                        <template v-if="!encode.passthrough"
                            ><span class="w-2" /><Toggle
                                :on="!!encode.slate_enabled"
                                label="Encode slate"
                                @click="toggleEncodeSlate"
                            /><span class="text-xs">slate {{ encode.slate_enabled ? 'on' : 'off' }}</span></template
                        >
                    </template>
                    <template v-if="output">
                        <Toggle :on="output.enabled" label="Enabled" @click="toggleOutput" /><span class="text-xs">{{
                            output.enabled ? 'enabled' : 'disabled'
                        }}</span>
                        <template v-if="output.kind === 'hdmi'"
                            ><span class="w-2" /><select
                                class="w-auto max-w-60 text-xs"
                                :value="output.input_id"
                                aria-label="On screen"
                                @change="showOnScreen(($event.target as HTMLSelectElement).value)"
                            >
                                <option value="">nothing (black)</option>
                                <option v-for="i in cfg.inputs" :key="i.id" :value="i.id">{{ i.name }}</option>
                            </select></template
                        >
                    </template>
                </fieldset>

                <!-- the state, on every tab: a slot of one height whatever it says, so
                     a warning coming and going moves nothing under the pointer -->
                <StatusSlot
                    v-if="snap && !machine && !storage"
                    class="mt-1.5"
                    :tone="note.tone"
                    :label="note.label"
                    :message="note.text"
                    data-test="detail-state"
                >
                    <span class="font-medium">{{ snap.state.replace('_', ' ') }}</span>
                    <span class="inline-block min-w-[7ch] text-fg-2 tabular-nums">{{ ago(snap.since, now) }}</span>
                    <span v-if="snap.restarts" class="text-fg-2 tabular-nums"
                        >· {{ snap.restarts }} restart{{ snap.restarts === 1 ? '' : 's' }}</span
                    >
                    <span
                        v-if="snap.reading && (encode || output)"
                        class="min-w-0 truncate text-warn"
                        data-test="reading"
                        >· reading {{ inputName(snap.reading) }}, its input's backup</span
                    >
                    <template #more>
                        <div v-if="encodeFaster.length" class="mt-2 flex flex-wrap gap-1.5" data-test="encode-faster">
                            <button
                                v-for="o in encodeFaster"
                                v-show="!node.readOnly"
                                :key="o.label"
                                type="button"
                                class="text-xs"
                                @click="useFaster(o.label)"
                            >
                                Use {{ o.label }}
                            </button>
                        </div>
                    </template>
                </StatusSlot>
                <div
                    v-if="failover && !virtualConfig"
                    class="mt-1 flex flex-wrap items-center gap-1.5 text-xs"
                    data-test="input-failover"
                >
                    <span v-if="failover.on_backup" class="text-warn"
                        >on its backup {{ inputName(failover.backup) }}
                        {{ failover.held ? '(by hand, until switched back)' : '' }}
                        <span v-if="failover.since" class="text-fg-2 tabular-nums">{{
                            ago(failover.since, now)
                        }}</span></span
                    >
                    <span v-else class="text-fg-2">backup: {{ inputName(failover.backup) }}</span>
                    <button
                        v-if="!node.readOnly"
                        type="button"
                        class="btn-ghost text-xs"
                        :disabled="switching"
                        data-test="failover-switch"
                        @click="useBackup(failover.on_backup ? 'main' : 'backup')"
                    >
                        {{ failover.on_backup ? 'Back to this input' : 'Use the backup' }}
                    </button>
                </div>
                <div
                    v-if="encode?.scte35 && !virtualConfig"
                    class="mt-1 flex flex-wrap items-center gap-1.5 text-xs"
                    data-test="encode-break"
                >
                    <span v-if="encSnap?.break" class="text-warn">{{ breakText(encSnap.break, now) }}</span>
                    <span v-else class="text-fg-2">{{
                        nextBreakText(encode.scte35.breaks, new Date(now)) || 'ad markers (SCTE-35)'
                    }}</span>
                    <template v-if="!node.readOnly">
                        <button
                            v-if="encSnap?.break && !encSnap.break.ending"
                            type="button"
                            class="btn-ghost text-xs"
                            :disabled="cueing"
                            data-test="break-end"
                            @click="cue('in')"
                        >
                            End the break
                        </button>
                        <template v-else-if="!encSnap?.break"
                            ><input
                                v-model.number="breakS"
                                type="number"
                                min="0"
                                max="86400"
                                step="15"
                                class="w-20 text-xs"
                                aria-label="Break length in seconds, 0 until ended"
                                title="the break's length in seconds; 0: until ended"
                                data-test="break-length"
                            /><span class="text-fg-2">s</span
                            ><button
                                type="button"
                                class="btn-ghost text-xs"
                                :disabled="cueing"
                                data-test="break-start"
                                @click="cue('out')"
                            >
                                Start an ad break
                            </button></template
                        >
                    </template>
                </div>
            </div>

            <DetailTabs v-if="tabs.length" v-model="tab" :tabs="tabs" :dots="dots" />
            <div class="px-3 py-2.5" :data-tab="tab">
                <MachineDetail
                    v-if="machine"
                    :tab="tab"
                    :machine="machine"
                    :series="series"
                    :times="times"
                    :graph-from="graphFrom"
                    :graph-label="graphLabel"
                />
                <MonitorDetail v-if="machine && tab === 'monitoring'" />

                <template v-if="tab === 'live'">
                    <!-- figures: three fixed columns, every cell there for its kind
                     whatever its value, so nothing reflows as figures change -->
                    <div v-if="snap && !machine && !storage" class="grid grid-cols-3 gap-x-3.5 gap-y-1.5">
                        <Figure v-if="encode || output" label="frame rate"
                            >{{ snap.fps?.toFixed(2) ?? '—' }} fps</Figure
                        >
                        <Figure v-if="output" label="dropped frames">{{ snap.dropped_frames ?? 0 }}</Figure>
                        <Figure v-if="output?.kind === 'hdmi'" label="decoder" :full="snap.decoder">{{
                            snap.decoder || '—'
                        }}</Figure>
                        <Figure
                            v-if="output?.kind === 'hdmi'"
                            label="a/v sync"
                            title="sound ahead of picture (negative: behind); mpv keeps them together, a gap that stays means it cannot"
                            ><span :class="Math.abs(snap.av_sync_ms ?? 0) > 100 ? 'text-warn' : ''">{{
                                snap.av_sync_ms === undefined
                                    ? '—'
                                    : (snap.av_sync_ms > 0 ? '+' : '') + snap.av_sync_ms.toFixed(0) + ' ms'
                            }}</span></Figure
                        >
                        <Figure
                            v-if="output?.kind === 'hdmi'"
                            label="buffer"
                            title="stream mpv holds before playing, and frames it showed late"
                            >{{ (snap.buffer_s ?? 0).toFixed(2) }} s<template #sub
                                >{{ snap.delayed_frames ?? 0 }} late</template
                            ></Figure
                        >
                        <Figure v-if="encode" label="bitrate"
                            >{{ mbps(snap.bitrate_k)
                            }}<template v-if="targetK" #sub>set {{ mbps(targetK) }}</template></Figure
                        >
                        <Figure
                            v-if="encode && !encode.passthrough"
                            label="quantizer"
                            title="the video's quantizer: lower is finer; it rises when the bitrate is short for the picture (busy scenes, noise)"
                            >{{ snap.quantizer ? snap.quantizer.toFixed(1) : '—' }}</Figure
                        >
                        <Figure
                            v-if="encode"
                            label="key frames"
                            title="time between key frames as they come out; players and platforms can only start on one"
                            >{{ snap.key_interval_s ? 'every ' + snap.key_interval_s.toFixed(1) + ' s' : '—'
                            }}<template v-if="gopSeconds" #sub>set {{ gopSeconds.toFixed(1) }} s</template></Figure
                        >
                        <Figure
                            v-if="encode && !encode.passthrough"
                            label="ffmpeg dup · drop"
                            title="frames ffmpeg itself duplicated or dropped to hold the frame rate"
                            >{{ snap.ffmpeg_dup ?? 0 }} · {{ snap.ffmpeg_drop ?? 0 }}</Figure
                        >
                        <Figure v-if="encode" label="buffer"
                            >{{ dur(Math.min(snap.ring_seconds, cfg.max_buffer_seconds))
                            }}<template #sub>of {{ dur(cfg.max_buffer_seconds) }}</template></Figure
                        >
                        <Figure v-if="encode" label="dropped frames">{{ snap.dropped_frames ?? 0 }}</Figure>
                        <Figure
                            v-if="encode && snap.captions"
                            label="captions"
                            :full="snap.captions"
                            data-test="encode-captions"
                            ><span :class="snap.captions === 'carried' ? '' : 'text-warn'">{{
                                snap.captions === 'carried' ? 'carried' : 'not carried'
                            }}</span
                            ><template v-if="snap.captions === 'carried'" #sub
                                >on {{ (snap.caption_frames ?? 0).toLocaleString() }} frames</template
                            ></Figure
                        >
                        <Figure
                            v-if="encode && snap.etr290"
                            label="TR 101 290"
                            title="its stream's MPEG-TS checks as it is written, since the encode started (2.3a counted, not warned of)"
                            :full="
                                [
                                    etrDescribe(snap.etr290) || 'no errors',
                                    snap.etr290_last && 'the last: ' + snap.etr290_last,
                                ]
                                    .filter(Boolean)
                                    .join('; ')
                            "
                            data-test="encode-etr290"
                            ><span :class="etrErrors(snap.etr290) ? 'text-warn' : ''">{{
                                etrErrors(snap.etr290) ? `${etrErrors(snap.etr290)} errors` : 'clean'
                            }}</span></Figure
                        >
                        <Figure
                            v-if="encode && snap.frame_aligned"
                            label="frames"
                            title="an encode of a frame-aligned input: on the input's frame numbers its timestamps and key frames are its 1+1 partner's, frame for frame"
                            data-test="encode-frame-aligned"
                            ><span :class="snap.frame_aligned === 'numbers' ? '' : 'text-warn'">{{
                                snap.frame_aligned === 'numbers' ? 'aligned' : 'not aligned yet'
                            }}</span
                            ><template v-if="snap.frame_aligned === 'waiting'" #sub
                                >the input's frames have no numbers</template
                            ></Figure
                        >
                        <Figure
                            v-if="encode && snap.source_cues"
                            label="source cues"
                            title="SCTE-35 its source sent, which it carries (each break is in the event log)"
                            :full="snap.source_cue_last"
                            data-test="encode-source-cues"
                            >{{ snap.source_cues.toLocaleString()
                            }}<template v-if="snap.source_cue_last" #sub>{{
                                snap.source_cue_last.split(' (')[0]
                            }}</template></Figure
                        >
                        <Figure
                            v-if="encode && probed(encode) && snap.probe"
                            label="probe"
                            title="what it sends, decoded: 10 s of black, the same picture or a silent track makes it degraded, as checked in its form"
                            data-test="encode-probe"
                            ><span :class="probeText(snap.probe, encode) === 'ok' ? '' : 'text-warn'">{{
                                probeText(snap.probe, encode)
                            }}</span></Figure
                        >
                        <Figure v-if="dest" label="sent">{{ bytes(snap.bytes_sent) }}</Figure>
                        <Figure
                            v-if="dest?.kind === 'file'"
                            label="free"
                            title="free space where it records, and how long that lasts at the encode's bitrate"
                            ><span :class="snap.hours_left && snap.hours_left < 1 ? 'text-warn' : ''">{{
                                snap.free_gb ? snap.free_gb.toFixed(snap.free_gb < 10 ? 1 : 0) + ' GB' : '—'
                            }}</span
                            ><template v-if="snap.hours_left" #sub
                                >{{
                                    snap.hours_left < 48
                                        ? snap.hours_left.toFixed(1) + ' h'
                                        : (snap.hours_left / 24).toFixed(0) + ' days'
                                }}
                                left</template
                            ></Figure
                        >
                        <Figure
                            v-if="dest?.kind === 'hls_push'"
                            label="segments put"
                            :title="`to ${snap.hls?.endpoint ?? ''}; the last one took ${snap.hls?.last_put_s?.toFixed(2) ?? '—'} s`"
                            data-test="hls-segments"
                            ><span :class="snap.hls?.waiting && snap.hls.waiting > 1 ? 'text-warn' : ''">{{
                                snap.hls?.segments ?? 0
                            }}</span
                            ><template #sub
                                >{{ snap.hls?.waiting ? `${snap.hls.waiting} waiting` : 'none waiting'
                                }}{{ snap.hls?.skipped ? ` · ${snap.hls.skipped} skipped` : '' }}</template
                            ></Figure
                        >
                        <Figure v-if="dest && countsReaders" label="clients"
                            >{{ snap.clients ?? 0
                            }}<template #sub>of {{ dest.max_clients || 'any number' }}</template></Figure
                        >
                        <Figure v-if="input" label="source format" :full="snap.source"
                            >{{ snap.source ? srcFmt[0] : input.kind === 'fake' ? 'generated' : 'waiting'
                            }}<template #sub>{{ srcFmt[1] }}</template></Figure
                        >
                        <Figure v-if="input?.source_timecode" label="source timecode">{{
                            snap.timecode || 'none'
                        }}</Figure>
                        <Figure v-if="input?.frame_aligned" label="frame-aligned" data-test="frame-aligned"
                            >{{
                                snap.frame_index === 'timecode'
                                    ? 'by timecode'
                                    : snap.frame_index === 'pts'
                                      ? "by the feed's PTS"
                                      : snap.frame_index === 'ptp'
                                        ? input.frame_shift
                                            ? 'by PTP, edge moved ½ frame'
                                            : 'by PTP'
                                        : 'no timecode, no PTP'
                            }}<template #sub
                                ><span
                                    v-if="snap.frame_index === 'ptp' && snap.frame_edge_ms != null"
                                    :class="{ 'text-warn': snap.frame_edge_ms < 2 }"
                                    :title="
                                        snap.frame_edge_ms < 2
                                            ? 'Near the edge between two frames: the pair\'s nodes may number them one apart'
                                            : undefined
                                    "
                                    >{{ snap.frame_edge_ms.toFixed(1) }} ms from a frame's edge</span
                                ><template v-else>{{
                                    snap.frame_index === 'timecode' || snap.frame_index === 'pts'
                                        ? 'frames numbered'
                                        : 'not aligned'
                                }}</template></template
                            ></Figure
                        >
                        <Figure v-if="input" label="frames received">{{
                            snap.received?.toLocaleString() ?? '—'
                        }}</Figure>
                        <Figure v-if="input" label="frames repeated">{{
                            snap.repeated?.toLocaleString() ?? '—'
                        }}</Figure>
                        <Figure
                            v-if="input?.kind === 'ndi'"
                            label="ndi lost"
                            title="the NDI receiver since capture started: frames lost because capture did not take them in time, frames waiting, and whether it is connected"
                            ><span :class="snap.ndi?.video_dropped ? 'text-warn' : ''">{{
                                snap.ndi ? snap.ndi.video_dropped.toLocaleString() : '—'
                            }}</span
                            ><template v-if="snap.ndi" #sub
                                >{{ snap.ndi.queued }} queued<span v-if="!snap.ndi.connections" class="text-bad">
                                    · not connected</span
                                ></template
                            ></Figure
                        >
                        <Figure
                            v-if="input?.kind === 'decklink'"
                            label="card overruns"
                            title="frames the card delivered while ffmpeg's queue was full: capture did not take them in time"
                            ><span :class="snap.capture_overruns ? 'text-warn' : ''">{{
                                (snap.capture_overruns ?? 0).toLocaleString()
                            }}</span></Figure
                        >
                        <Figure v-if="hasCPU" label="cpu, of the machine"
                            >{{ cpuPct(share(snap.cpu_cores ?? 0))
                            }}<template #sub
                                >{{ (snap.cpu_cores ?? 0).toFixed(1) }} of {{ machineCores }} cores</template
                            ></Figure
                        >
                        <Figure v-if="encode && snap.gpu" :label="`gpu ${snap.gpu.gpu}`"
                            >nvenc {{ snap.gpu.enc }}%<template #sub
                                >3d {{ snap.gpu.sm }}% · nvdec {{ snap.gpu.dec }}%</template
                            ></Figure
                        >
                        <Figure v-if="input && snap.gpu" :label="`gpu ${snap.gpu.gpu}, decoding`"
                            >nvdec {{ snap.gpu.dec }}%<template #sub>3d {{ snap.gpu.sm }}%</template></Figure
                        >
                    </div>

                    <!-- an SRT link and MPEG-TS errors (ffmpeg-ese.patch) -->
                    <LinkStats
                        v-if="(input || dest || output) && (snap.srt || snap.ts_errors || snap.socket)"
                        :srt="snap.srt"
                        :ts="snap.ts_errors"
                        :sock="snap.socket"
                        :sending="!!dest"
                    />

                    <!-- preview and audio peaks per source channel -->
                    <InputPreview v-if="input" :input="input" :snap="snap" :passed-by="passedBy" />
                    <StreamPreview v-if="encode && encode.enabled" :id="encode.id" kind="encodes" />
                    <!-- MCR monitoring: what the encode sends, decoded, with its tracks to hear -->
                    <MonitorPlayer
                        v-if="encode && encode.enabled && !virtualConfig && node.config?.mode !== 'player'"
                        :key="'mon-e-' + encode.id"
                        :source="'encode/' + encode.id"
                        :pair-labels="encode.audio.slice(0, 8).map((a, i) => a.name || 'track ' + (i + 1))"
                    />
                    <StreamPreview
                        v-if="dest"
                        :id="dest.id"
                        :key="dest.id"
                        kind="destinations"
                        :delay="dest.delay_seconds"
                    />
                    <MonitorPlayer
                        v-if="dest && destEncode && !virtualConfig && node.config?.mode !== 'player'"
                        :key="'mon-d-' + dest.id"
                        :source="'destination/' + dest.id"
                        :pair-labels="destEncode.audio.slice(0, 8).map((a, i) => a.name || 'track ' + (i + 1))"
                    />
                    <SourceAudio v-if="input && snap.peak_db?.length" :peak="snap.peak_db" :loudness="snap.loudness" />
                    <!-- MCR monitoring: the input live with one pair to hear, over WebRTC -->
                    <MonitorPlayer
                        v-if="input && !virtualConfig && node.config?.mode !== 'player'"
                        :key="'mon-' + input.id"
                        :source="'input/' + input.id"
                        :channels="snap.peak_db?.length"
                    />
                    <div v-if="pushTargets.length" class="mt-3 rounded-md border border-line px-2.5 py-2">
                        <div class="box-title">Push your feed to</div>
                        <div v-for="t in pushTargets" :key="t.url" class="flex items-center gap-1.5">
                            <span class="w-12 text-xs text-fg-3">{{ t.label }}</span
                            ><code class="min-w-0 flex-1 truncate font-mono text-xs tabular-nums">{{
                                maskURL(t.url)
                            }}</code
                            ><button class="btn-ghost text-xs" @click="copy(t.url)">copy</button>
                        </div>
                        <div class="text-xs text-fg-3">
                            Public is the address this node calls out from (the port must be forwarded to it); private
                            are its interfaces. Use the one your source can reach.
                        </div>
                    </div>
                    <div
                        v-else-if="
                            input && (input.kind === 'srt_caller' || input.kind === 'rtmp_pull' || input.kind === 'url')
                        "
                        class="mt-3 rounded-md border border-line px-2.5 py-2"
                    >
                        <div class="box-title">This node connects out to</div>
                        <code class="font-mono text-xs tabular-nums">{{ maskURL(input.url ?? '') }}</code>
                    </div>

                    <!-- an rtp destination's two paths (2022-7): what each took -->
                    <div
                        v-if="dest?.kind === 'rtp' && snap.paths?.length"
                        class="mt-3 rounded-md border border-line px-2.5 py-2"
                        data-test="rtp-paths"
                    >
                        <div class="text-xs text-fg-3">
                            SMPTE 2022-7: every packet on both paths{{
                                dest.fec ? ` · FEC ${dest.fec.columns}×${dest.fec.rows}` : ''
                            }}
                        </div>
                        <div
                            v-for="(p, i) in snap.paths"
                            :key="p.address + i"
                            class="mt-1 flex flex-wrap items-baseline gap-x-2 text-xs"
                        >
                            <b>{{ i === 0 ? 'path 1' : 'path 2' }}</b>
                            <code class="font-mono tabular-nums">{{ p.address }}</code>
                            <span v-if="p.interface" class="text-fg-3">from {{ p.interface }}</span>
                            <span class="font-mono tabular-nums">{{ p.packets.toLocaleString() }} packets</span>
                            <span v-if="p.failed" class="text-bad" :title="p.last_error"
                                >{{ p.failed.toLocaleString() }} not sent: {{ p.last_error }}</span
                            >
                        </div>
                    </div>

                    <!-- pull destinations: where readers connect -->
                    <div v-if="dest && isPullDest" class="mt-3 rounded-md border border-line px-2.5 py-2">
                        <div class="text-xs text-fg-3">
                            Pull from · port {{ snap.port ?? '—'
                            }}<template v-if="snap.pull_error">
                                · <span class="text-bad">pull server: {{ snap.pull_error }}</span></template
                            ><template v-else-if="snap.port && !snap.pull_running">
                                · <span class="text-warn">pull server starting</span></template
                            >
                        </div>
                        <div v-for="t in pullTargets" :key="t.url" class="flex items-center gap-1.5">
                            <span class="w-12 text-xs text-fg-3">{{ t.label }}</span
                            ><code class="min-w-0 flex-1 truncate font-mono text-xs tabular-nums">{{
                                maskURL(t.url)
                            }}</code
                            ><button class="btn-ghost text-xs" @click="copy(t.url)">copy</button>
                        </div>
                        <div class="text-xs text-fg-3">
                            {{
                                dest.kind === 'rtmp_pull'
                                    ? 'Any RTMP player or ingest can pull this; credentials travel in the URL.'
                                    : dest.kind === 'hls_pull'
                                      ? "HLS players and CDNs read the playlist; the stream's folder without index.m3u8 is a page that plays it. Plain HTTP: a CDN or a TLS proxy in front for the internet."
                                      : 'SRT callers use streamid=read:' +
                                        (dest.stream || dest.id) +
                                        (dest.pull_auth ? ':user:pass' : '') +
                                        ' on the shared SRT port, and set their own latency: latency= in the URL (microseconds in ffmpeg, milliseconds in VLC), the Latency field in vMix. This node offers ' +
                                        (snap.pull_latency_ms || 120) +
                                        ' ms, which a reader asking less gets; on a long or lossy path ask more, e.g. 120 ms (latency=120000 in ffmpeg).'
                            }}
                            Addresses are this machine's interfaces; use the one your receiver can reach.
                        </div>
                    </div>

                    <!-- encode: what each output track carries -->
                    <div
                        v-if="encode && !encode.passthrough && trackMeters.length"
                        class="mt-3 rounded-md border border-line px-2.5 py-2"
                    >
                        <div class="box-title" title="What each output track carries from the source">
                            Output tracks<span class="font-normal tracking-normal normal-case">dBFS peak</span>
                        </div>
                        <div v-for="t in trackMeters" :key="t.name" class="mt-1.5">
                            <div class="text-xs">
                                {{ t.name }} <span class="text-fg-2">{{ t.layout }}</span>
                            </div>
                            <Meters :db="t.db" :labels="t.labels" />
                        </div>
                    </div>
                </template>

                <CasterStats
                    v-if="tab === 'caster' && input?.kind === 'caster'"
                    :st="snap.caster"
                    :error="snap.caster_error"
                    :return-encoder="snap.return_encoder"
                    :return-shared-with="snap.return_shared_with"
                    :input-id="input.id"
                    :peak="snap.peak_db"
                />

                <template v-if="tab === 'audio' && input">
                    <SourceAudio v-if="snap.peak_db?.length" :peak="snap.peak_db" :loudness="snap.loudness" />
                    <AudioHistory
                        v-if="measured(series)"
                        :samples="series"
                        :from="graphFrom"
                        :to="node.span[1]"
                        :label="`history · loudest per channel · ${graphLabel}`"
                    />
                    <div v-else-if="!seriesLoading" class="mt-3 text-xs text-fg-2">
                        No audio history in this window (it is kept from the version that measures it on).
                    </div>
                </template>

                <!-- graphs -->
                <div
                    v-if="tab === 'graphs' && !machine && !seriesLoading && series.length < 2"
                    class="text-xs text-fg-2"
                >
                    No samples in this window yet.
                </div>
                <div v-if="tab === 'graphs' && series.length > 1 && !machine" class="mt-1.5">
                    <Sparkline
                        v-if="!input && !output"
                        :loading="seriesLoading"
                        :label="`bitrate · ${graphLabel}`"
                        :times="times"
                        :from="graphFrom"
                        :to="node.span[1]"
                        :values="series.map((s) => s.bitrate_k ?? 0)"
                        :format="mbps"
                    />
                    <Sparkline
                        v-if="dest && countsReaders"
                        :loading="seriesLoading"
                        label="clients"
                        :times="times"
                        :from="graphFrom"
                        :to="node.span[1]"
                        :values="series.map((s) => s.clients ?? 0)"
                        :format="(v) => (Number.isInteger(v) ? String(v) : v.toFixed(1))"
                        color="var(--ok)"
                    />
                    <Sparkline
                        v-if="encode || output"
                        :loading="seriesLoading"
                        :label="output ? `frame rate · ${graphLabel}` : 'frame rate'"
                        :times="times"
                        :from="graphFrom"
                        :to="node.span[1]"
                        :values="series.map((s) => s.fps ?? 0)"
                        :format="(v) => v.toFixed(1) + ' fps'"
                        color="var(--ok)"
                    />
                    <Sparkline
                        v-if="encode && series.some((s) => s.q)"
                        :loading="seriesLoading"
                        label="quantizer · lower is finer"
                        :times="times"
                        :from="graphFrom"
                        :to="node.span[1]"
                        :values="series.map((s) => s.q ?? 0)"
                        :format="(v) => v.toFixed(1)"
                    />
                    <Sparkline
                        v-if="encode || output"
                        :loading="seriesLoading"
                        label="dropped frames / s"
                        :times="times"
                        :from="graphFrom"
                        :to="node.span[1]"
                        :values="series.map((s) => s.dropped ?? 0)"
                        color="var(--bad)"
                    />
                    <Sparkline
                        v-if="hasCPU"
                        :loading="seriesLoading"
                        label="cpu, of the whole machine"
                        :times="times"
                        :from="graphFrom"
                        :to="node.span[1]"
                        :max="100"
                        :values="series.map((s) => share(s.cpu ?? 0))"
                        :format="cpuPct"
                    />
                    <Sparkline
                        v-if="input && series.some((s) => s.gpu)"
                        :loading="seriesLoading"
                        label="nvdec"
                        :max="100"
                        :times="times"
                        :from="graphFrom"
                        :to="node.span[1]"
                        :values="series.map((s) => s.gpu ?? 0)"
                        :format="(v) => v.toFixed(0) + ' %'"
                        color="var(--ok)"
                    />
                    <Sparkline
                        v-if="encode && series.some((s) => s.gpu)"
                        :loading="seriesLoading"
                        label="nvenc"
                        :max="100"
                        :times="times"
                        :from="graphFrom"
                        :to="node.span[1]"
                        :values="series.map((s) => s.gpu ?? 0)"
                        :format="(v) => v.toFixed(0) + ' %'"
                        color="var(--ok)"
                    />
                    <Sparkline
                        v-if="input"
                        :loading="seriesLoading"
                        label="frames from source / s"
                        :times="times"
                        :from="graphFrom"
                        :to="node.span[1]"
                        :values="series.map((s) => s.fps ?? 0)"
                        :format="(v) => v.toFixed(0) + ' fps'"
                        color="var(--ok)"
                    />
                    <Sparkline
                        v-if="input"
                        :loading="seriesLoading"
                        label="frames repeated / s"
                        :times="times"
                        :from="graphFrom"
                        :to="node.span[1]"
                        :values="series.map((s) => s.dropped ?? 0)"
                        color="var(--bad)"
                    />
                    <template v-if="hasLink">
                        <Sparkline
                            :loading="seriesLoading"
                            :label="`${linkName} round trip`"
                            :times="times"
                            :from="graphFrom"
                            :to="node.span[1]"
                            :values="series.map((s) => s.rtt_ms ?? 0)"
                            :format="(v) => v.toFixed(v < 10 ? 1 : 0) + ' ms'"
                        />
                        <Sparkline
                            :loading="seriesLoading"
                            :label="
                                linkName === 'TCP'
                                    ? 'TCP segments resent / s'
                                    : `${linkName} packets lost / s · resent in time`
                            "
                            :times="times"
                            :from="graphFrom"
                            :to="node.span[1]"
                            :values="series.map((s) => s.lost ?? 0)"
                            color="var(--warn)"
                        />
                        <Sparkline
                            v-if="linkName !== 'TCP'"
                            :loading="seriesLoading"
                            :label="`${linkName} packets lost for good / s`"
                            :times="times"
                            :from="graphFrom"
                            :to="node.span[1]"
                            :values="series.map((s) => s.link_dropped ?? 0)"
                            color="var(--bad)"
                        />
                    </template>
                    <Sparkline
                        v-if="input && series.some((s) => s.source_lost)"
                        :loading="seriesLoading"
                        label="frames from the source lost / s"
                        :times="times"
                        :from="graphFrom"
                        :to="node.span[1]"
                        :values="series.map((s) => s.source_lost ?? 0)"
                        color="var(--bad)"
                    />
                    <Sparkline
                        v-if="series.some((s) => s.udp_drops)"
                        :loading="seriesLoading"
                        label="UDP packets dropped before read / s"
                        :times="times"
                        :from="graphFrom"
                        :to="node.span[1]"
                        :values="series.map((s) => s.udp_drops ?? 0)"
                        color="var(--bad)"
                    />
                    <Sparkline
                        v-if="series.some((s) => s.ts_errors)"
                        :loading="seriesLoading"
                        label="MPEG-TS errors / s"
                        :times="times"
                        :from="graphFrom"
                        :to="node.span[1]"
                        :values="series.map((s) => s.ts_errors ?? 0)"
                        color="var(--bad)"
                    />
                </div>

                <PullClients v-if="tab === 'clients' && dest && countsReaders" :id="dest.id" :key="dest.id" />

                <!-- events -->
                <template v-if="tab === 'events' || (!tabs.length && !machine)">
                    <div class="mb-1 text-xs text-fg-2" data-test="detail-events">
                        Events · {{ 'preset' in node.window ? 'last ' + node.window.preset : 'selected window' }}
                    </div>
                    <div v-if="!events.length" class="text-xs text-fg-2">Nothing in this range.</div>
                    <div
                        v-for="e in events"
                        :key="e.id"
                        class="grid cursor-pointer grid-cols-[148px_8px_1fr] items-center gap-2 border-t border-line py-1 text-xs hover:bg-bg-2"
                        role="button"
                        tabindex="0"
                        @click="openEvent = e"
                        @keydown.enter.prevent="openEvent = e"
                        @keydown.space.prevent="openEvent = e"
                    >
                        <span class="font-mono text-fg-2 tabular-nums"
                            ><span v-if="Date.parse(e.ts) < node.span[0]" class="font-sans text-warn">since </span
                            >{{ dateTime(e.ts) }}</span
                        >
                        <span :class="e.state ? dotClass(e.state) : dot('ok')" />
                        <span class="truncate" :title="maskURLs(e.message)">{{ maskURLs(e.message) }}</span>
                    </div>
                </template>
            </div>
        </template>
        <ConfirmDialog
            v-if="confirm"
            :title="confirm.title"
            :body="confirm.body"
            :confirm-label="confirm.label"
            :danger="confirm.danger"
            @confirm="runConfirm"
            @cancel="confirm = null"
        />
        <EventDialog
            v-if="openEvent"
            :events="[openEvent]"
            :from="Date.parse(openEvent.ts)"
            :to="Date.parse(openEvent.ts)"
            :title="entityLabel(cfg, openEvent.kind, openEvent.entity)"
            :all="node.events"
            @close="openEvent = null"
        />
    </div>
</template>
